import { TrocaInicioPonto } from '@/service/api'
import { PeriodoPontoFactory } from '@acme/common'
import dayjs, { Dayjs } from 'dayjs'

export type TipoCompetencia = 'atual' | 'transicao' | 'futuro'

export interface CompetenciaCalculada {
    /** 'YYYY-MM' */
    competencia: string
    inicio: Dayjs
    fim: Dayjs
    dias: number
    tipo: TipoCompetencia
    /** competência mais longa que o normal (transição) */
    isTransicao: boolean
}

interface Params {
    /** 'YYYY-MM' */
    competenciaAtual: string
    /** dia de início vigente (anterior à troca) */
    diaInicioPonto: number
    /** novo dia de início escolhido */
    novoDiaInicioPonto: number
    /** tamanho fixo da competência atual (default 31) */
    maximoDias?: number
    trocasInicioPonto: TrocaInicioPonto[]
}

const DIAS_NORMAIS = 31

function diasEntre(a: Dayjs, b: Dayjs): number {
    return b.diff(a, 'day') + 1
}

/**
 * Calcula as 3 competências (atual, transição, futura) a partir do novo dia de
 * início. Função pura, sem efeitos colaterais — quem chama decide o que fazer
 * com o resultado (ex.: alimentar o form ou montar a prévia de fechamentos).
 */
export function calcularCompetencias({
    competenciaAtual,
    diaInicioPonto,
    novoDiaInicioPonto,
    maximoDias = 31,
    trocasInicioPonto,
}: Params): CompetenciaCalculada[] {
    const competencia = dayjs.utc(competenciaAtual, 'YYYY-MM')
    const trocasDate = trocasInicioPonto.map(it => ({
        ...it,
        dataInicial: dayjs.utc(it.dataInicial).toDate(),
        dataFinal: dayjs.utc(it.dataFinal).toDate(),
    }))

    const [inicio] = PeriodoPontoFactory.fromCompetencia(
        competencia.format('YYYY-MM'),
        { diaInicioPonto, trocasInicioPonto: trocasDate },
    ).getPeriodo()

    const fim = inicio.add(maximoDias, 'days').subtract(1, 'day')

    const proximoInicio = fim.add(1, 'day')
    // A transição vai até a véspera do próximo "dia de início" (novoDia): a
    // competência futura começa no primeiro dia `novoDia` posterior ao início
    // da transição — no mesmo mês se o dia ainda não passou, senão no seguinte.
    // Assim o novo ciclo sempre cai exatamente no dia escolhido, e a transição
    // nunca passa de ~31 dias (logo, nunca estoura o limite de 45).
    let inicioFutura = proximoInicio.set('date', Number(novoDiaInicioPonto))
    if (!inicioFutura.isAfter(proximoInicio)) {
        inicioFutura = proximoInicio
            .add(1, 'month')
            .set('date', Number(novoDiaInicioPonto))
    }
    const proximoFim = inicioFutura.subtract(1, 'day')

    const inicioSeguinte = proximoFim.add(1, 'day')
    const fimSeguinte = inicioSeguinte
        .set('date', Number(novoDiaInicioPonto))
        .set('month', inicioSeguinte.month() + 1)
        .subtract(1, 'day')

    return [
        {
            competencia: competencia.format('YYYY-MM'),
            inicio,
            fim,
            dias: diasEntre(inicio, fim),
            tipo: 'atual',
            isTransicao: diasEntre(inicio, fim) > DIAS_NORMAIS,
        },
        {
            competencia: competencia.add(1, 'month').format('YYYY-MM'),
            inicio: proximoInicio,
            fim: proximoFim,
            dias: diasEntre(proximoInicio, proximoFim),
            tipo: 'transicao',
            isTransicao: diasEntre(proximoInicio, proximoFim) > DIAS_NORMAIS,
        },
        {
            competencia: competencia.add(2, 'month').format('YYYY-MM'),
            inicio: inicioSeguinte,
            fim: fimSeguinte,
            dias: diasEntre(inicioSeguinte, fimSeguinte),
            tipo: 'futuro',
            isTransicao: diasEntre(inicioSeguinte, fimSeguinte) > DIAS_NORMAIS,
        },
    ]
}
